/**
 * /saas — SaaS plans, subscriptions, and metrics admin.
 *
 * Three tabs (local state, no URL routing — matches /invoices pattern):
 *   - Plans          : list / create / cascade-delete plans
 *   - Subscriptions  : list / create / cancel / invoice-now / delete
 *   - Metrics        : live MRR + ARR, snapshot history, take-snapshot,
 *                      run-billing-cycle
 *
 * Plans are the catalog (fixed prices); Subscriptions are customers
 * subscribed to one or more plans. The MRR / ARR live on the active set.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Tabs, Tab, Chip } from "@mui/material";
import {
  DataGridPro,
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";

import { FlexGrid, gridDefaults } from "@/grids/gridDefaults";
import { api } from "@/lib/api";
import {
  partiesListQueryOptions,
  saasArrQueryOptions,
  saasKeys,
  saasMrrQueryOptions,
  saasPlansQueryOptions,
  saasSnapshotsQueryOptions,
  saasSubscriptionsQueryOptions,
  saasTaxRulesQueryOptions,
} from "@/lib/queries";
import { PlanFormDialog } from "@/components/PlanFormDialog";
import { SubscriptionFormDialog } from "@/components/SubscriptionFormDialog";
import { useConfirmAction } from "@/components/ConfirmDialog";
import { useToast } from "@/components/Toaster";
import type {
  MetricsSnapshot,
  Party,
  Plan,
  PlanStatus,
  Subscription,
  SubscriptionStatus,
  TaxRule,
  TaxRuleCreate,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/saas/")({
  component: SaasPage,
});

type TabKey = "plans" | "subscriptions" | "metrics" | "revrec" | "tax";

function SaasPage() {
  const [tab, setTab] = useState<TabKey>("plans");

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">SaaS</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Plans · Subscriptions · MRR / ARR
          </span>
        </div>
      </div>

      <div className="bg-white border-b border-slate-200 px-6 dark:bg-gray-900 dark:border-gray-800">
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v as TabKey)}
          sx={{ minHeight: 40 }}
        >
          <Tab value="plans" label="Plans" sx={{ minHeight: 40 }} />
          <Tab value="subscriptions" label="Subscriptions" sx={{ minHeight: 40 }} />
          <Tab value="metrics" label="Metrics" sx={{ minHeight: 40 }} />
          <Tab value="revrec" label="Revenue Recognition" sx={{ minHeight: 40 }} />
          <Tab value="tax" label="Tax Rules" sx={{ minHeight: 40 }} />
        </Tabs>
      </div>

      {/* overflow-hidden + flex-1 lets each tab own its scroll surface
          so the grid header stays pinned. The tab's grid takes remaining
          vertical space; only the grid's row body scrolls. */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {tab === "plans" && <PlansTab />}
        {tab === "subscriptions" && <SubscriptionsTab />}
        {tab === "metrics" && <MetricsTab />}
        {tab === "revrec" && <RevRecTab />}
        {tab === "tax" && <TaxRulesTab />}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Plans tab
// ──────────────────────────────────────────────────────────────
const PLAN_STATUS_COLOR: Record<PlanStatus, "success" | "warning" | "default"> = {
  active: "success",
  grandfathered: "warning",
  archived: "default",
};

function PlansTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirmAction();

  const plansQuery = useQuery(saasPlansQueryOptions());
  const [formOpen, setFormOpen] = useState(false);

  const deletePlan = useMutation({
    mutationFn: async ({ id, force }: { id: string; force: boolean }) => {
      await api(`/api/saas/plans/${id}${force ? "?force=true" : ""}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: saasKeys.all });
      toast.success("Plan deleted");
    },
    onError: (err: Error) => toast.error(`Delete failed: ${err.message}`),
  });

  const requestDelete = (p: Plan) => {
    confirmAction({
      title: `Delete plan ${p.code}?`,
      message:
        `${p.code} — ${p.name}

` +
        `If subscriptions reference this plan, the API returns 409. ` +
        `Confirm-and-force will cascade-delete every subscription + ` +
        `recognition schedule (posted journals are NOT touched — they're immutable).`,
      confirmLabel: "Delete (force)",
      kind: "danger",
      onConfirm: async () => {
        await deletePlan.mutateAsync({ id: p.id, force: true });
      },
    });
  };

  const columns = useMemo<GridColDef<Plan>[]>(() => [
    { field: "code", headerName: "Code", width: 160 },
    { field: "name", headerName: "Name", width: 240 },
    {
      field: "amount",
      headerName: "Price",
      width: 140,
      renderCell: (p: GridRenderCellParams<Plan>) => {
        const r = p.row;
        const interval =
          r.interval_count === 1
            ? `/ ${r.interval}`
            : `/ ${r.interval_count} ${r.interval}s`;
        return (
          <span className="font-mono text-sm">
            {Number(r.amount).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{" "}
            {r.currency_code}
            <span className="text-xs text-slate-500 ml-1 dark:text-slate-400">{interval}</span>
          </span>
        );
      },
    },
    {
      field: "trial_days",
      headerName: "Trial",
      width: 80,
      renderCell: (p) =>
        p.row.trial_days > 0 ? `${p.row.trial_days} d` : "—",
    },
    {
      field: "is_metered",
      headerName: "Type",
      width: 110,
      renderCell: (p: GridRenderCellParams<Plan>) =>
        p.row.is_metered ? (
          <Chip
            label={`metered · ${p.row.unit_label ?? "unit"}`}
            size="small"
            color="info"
            sx={{ fontSize: 10, height: 20 }}
          />
        ) : (
          <Chip
            label="flat"
            size="small"
            variant="outlined"
            sx={{ fontSize: 10, height: 20 }}
          />
        ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (p: GridRenderCellParams<Plan>) => (
        <Chip
          label={p.row.status}
          size="small"
          color={PLAN_STATUS_COLOR[p.row.status]}
          sx={{ fontSize: 10, height: 20 }}
        />
      ),
    },
    {
      field: "external_ref",
      headerName: "External ref",
      width: 200,
      renderCell: (p) => (
        <span className="font-mono text-xs text-slate-500 dark:text-slate-400">
          {p.row.external_ref ?? "—"}
        </span>
      ),
    },
    {
      field: "_actions",
      headerName: "",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (p: GridRenderCellParams<Plan>) => (
        <div className="flex items-center gap-3">
          <Link
            to="/saas/plans/$planId"
            params={{ planId: p.row.id }}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Pricing
          </Link>
          <button
            onClick={() => requestDelete(p.row)}
            className="text-xs text-red-600 hover:text-red-800"
          >
            Delete
          </button>
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    // Flex column: toolbar at top stays fixed; grid below takes remaining
    // height with internal scrolling so column headers stay pinned.
    <div className="flex flex-col h-full px-6 py-4 gap-3">
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-sm text-slate-500 dark:text-slate-400">
          {plansQuery.data?.length ?? 0} plan
          {plansQuery.data?.length === 1 ? "" : "s"}
        </span>
        <div className="flex-1" />
        <button
          onClick={() => setFormOpen(true)}
          className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium"
        >
          + New plan
        </button>
      </div>

      <PlanFormDialog open={formOpen} onClose={() => setFormOpen(false)} />

      <div className="flex-1 min-h-0">
        <FlexGrid>
          <DataGridPro
            {...gridDefaults}
            rows={plansQuery.data ?? []}
            columns={columns}
            getRowId={(r) => r.id}
            loading={plansQuery.isLoading}
            density="compact"
            disableRowSelectionOnClick
          />
        </FlexGrid>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Subscriptions tab
// ──────────────────────────────────────────────────────────────
const SUB_STATUS_COLOR: Record<SubscriptionStatus, "success" | "warning" | "error" | "default"> = {
  trialing: "warning",
  active: "success",
  past_due: "error",
  paused: "warning",
  canceled: "default",
  expired: "default",
};

function SubscriptionsTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirmAction();

  const subscriptionsQuery = useQuery(saasSubscriptionsQueryOptions());
  const plansQuery = useQuery(saasPlansQueryOptions());
  const customersQuery = useQuery(
    partiesListQueryOptions({ party_type: "customer", active_only: false }),
  );

  const customersById = useMemo(() => {
    const m: Record<string, Party> = {};
    (customersQuery.data ?? []).forEach((c) => { m[c.id] = c; });
    return m;
  }, [customersQuery.data]);
  const plansById = useMemo(() => {
    const m: Record<string, Plan> = {};
    (plansQuery.data ?? []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [plansQuery.data]);

  const [formOpen, setFormOpen] = useState(false);

  const cancelSub = useMutation({
    mutationFn: async (vars: { id: string; immediate: boolean }) =>
      api(`/api/saas/subscriptions/${vars.id}/cancel`, {
        method: "POST",
        body: { immediate: vars.immediate, cancel_date: null },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: saasKeys.all });
      toast.success("Subscription canceled");
    },
    onError: (err: Error) => toast.error(`Cancel failed: ${err.message}`),
  });

  const invoiceNow = useMutation({
    mutationFn: async (id: string) =>
      api<{ ar_invoice_id: string; total: string }>(
        `/api/saas/subscriptions/${id}/invoice`,
        { method: "POST" },
      ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: saasKeys.all });
      toast.success(`Invoice created: ${r.total}`);
    },
    onError: (err: Error) => toast.error(`Invoice failed: ${err.message}`),
  });

  const deleteSub = useMutation({
    mutationFn: async ({ id, force }: { id: string; force: boolean }) => {
      await api(`/api/saas/subscriptions/${id}${force ? "?force=true" : ""}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: saasKeys.all });
      toast.success("Subscription deleted");
    },
    onError: (err: Error) => toast.error(`Delete failed: ${err.message}`),
  });

  const requestCancel = (s: Subscription) => {
    confirmAction({
      title: `Cancel ${s.subscription_no}?`,
      message:
        `Customer: ${customersById[s.customer_id]?.name ?? "?"}

` +
        `Cancel IMMEDIATELY (effective today, no further billing) — ` +
        `or cancel at end of period (current period runs out, then expires).

` +
        `Currently this dialog cancels at end of period. Use the API for immediate cancellation.`,
      confirmLabel: "Cancel at period end",
      kind: "danger",
      onConfirm: async () => {
        await cancelSub.mutateAsync({ id: s.id, immediate: false });
      },
    });
  };

  const requestDelete = (s: Subscription) => {
    confirmAction({
      title: `Delete ${s.subscription_no}?`,
      message:
        `Hard-delete the subscription, items, and recognition schedules.

` +
        `Active subscriptions require ?force=true (this confirms it). ` +
        `Posted journals are NOT touched (immutable by design).`,
      confirmLabel: "Delete (force)",
      kind: "danger",
      onConfirm: async () => {
        await deleteSub.mutateAsync({ id: s.id, force: true });
      },
    });
  };

  const columns = useMemo<GridColDef<Subscription>[]>(() => [
    { field: "subscription_no", headerName: "Sub #", width: 160 },
    {
      field: "customer_id",
      headerName: "Customer",
      width: 220,
      valueGetter: (_v, r) =>
        customersById[r.customer_id]?.name ?? r.customer_id,
    },
    {
      field: "items",
      headerName: "Plan(s)",
      width: 280,
      renderCell: (p: GridRenderCellParams<Subscription>) => (
        <div className="text-xs">
          {p.row.items.map((it) => {
            const plan = plansById[it.plan_id];
            return (
              <div key={it.id}>
                {plan ? plan.name : it.plan_id}
                {it.quantity > 1 && (
                  <span className="text-slate-500 dark:text-slate-400"> × {it.quantity}</span>
                )}
              </div>
            );
          })}
        </div>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (p: GridRenderCellParams<Subscription>) => (
        <Chip
          label={p.row.status}
          size="small"
          color={SUB_STATUS_COLOR[p.row.status]}
          sx={{ fontSize: 10, height: 20 }}
        />
      ),
    },
    {
      field: "current_period_start",
      headerName: "Period",
      width: 200,
      renderCell: (p: GridRenderCellParams<Subscription>) => (
        <span className="text-xs text-slate-700 dark:text-slate-300">
          {p.row.current_period_start} → {p.row.current_period_end}
        </span>
      ),
    },
    {
      field: "next_invoice_date",
      headerName: "Next bill",
      width: 110,
      renderCell: (p) => (
        <span className="text-xs">{p.row.next_invoice_date ?? "—"}</span>
      ),
    },
    {
      field: "discount_percent",
      headerName: "Disc.",
      width: 70,
      renderCell: (p) => {
        const d = Number(p.row.discount_percent);
        return d > 0 ? <span className="text-amber-700 text-xs">{d}%</span> : "—";
      },
    },
    {
      field: "_actions",
      headerName: "",
      width: 240,
      sortable: false,
      filterable: false,
      renderCell: (p: GridRenderCellParams<Subscription>) => {
        const s = p.row;
        const isActive = s.status === "active" || s.status === "trialing";
        return (
          <div className="flex items-center gap-2">
            {isActive && (
              <button
                onClick={() => invoiceNow.mutate(s.id)}
                disabled={invoiceNow.isPending}
                className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
                title="Generate AR invoice for current period now"
              >
                Invoice
              </button>
            )}
            {isActive && (
              <button
                onClick={() => requestCancel(s)}
                className="text-xs text-amber-700 hover:text-amber-900"
              >
                Cancel
              </button>
            )}
            <button
              onClick={() => requestDelete(s)}
              className="text-xs text-red-600 hover:text-red-800"
            >
              Delete
            </button>
          </div>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [customersById, plansById, invoiceNow, cancelSub, deleteSub]);

  return (
    <div className="flex flex-col h-full px-6 py-4 gap-3">
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-sm text-slate-500 dark:text-slate-400">
          {subscriptionsQuery.data?.length ?? 0} subscription
          {subscriptionsQuery.data?.length === 1 ? "" : "s"}
        </span>
        <div className="flex-1" />
        <button
          onClick={() => setFormOpen(true)}
          className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium"
        >
          + New subscription
        </button>
      </div>

      <SubscriptionFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
      />

      <div className="flex-1 min-h-0">
        <FlexGrid>
          <DataGridPro
            {...gridDefaults}
            rows={subscriptionsQuery.data ?? []}
            columns={columns}
            getRowId={(r) => r.id}
            loading={subscriptionsQuery.isLoading}
            density="compact"
            disableRowSelectionOnClick
          />
        </FlexGrid>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Metrics tab — live MRR/ARR + snapshot history + run-billing button
// ──────────────────────────────────────────────────────────────
function MetricsTab() {
  const qc = useQueryClient();
  const toast = useToast();

  const mrrQuery = useQuery(saasMrrQueryOptions());
  const arrQuery = useQuery(saasArrQueryOptions());
  const snapshotsQuery = useQuery(saasSnapshotsQueryOptions(90));

  const takeSnapshot = useMutation({
    mutationFn: async () =>
      api<MetricsSnapshot>("/api/saas/metrics/snapshot", { method: "POST" }),
    onSuccess: (snap) => {
      qc.invalidateQueries({ queryKey: saasKeys.all });
      toast.success(
        `Snapshot taken — MRR ${Number(snap.mrr).toFixed(2)} ${snap.base_currency_code}`,
      );
    },
    onError: (err: Error) => toast.error(`Snapshot failed: ${err.message}`),
  });

  const runBilling = useMutation({
    mutationFn: async () =>
      api<{ invoiced_count: number; ar_invoice_ids: string[] }>(
        "/api/saas/billing/run",
        { method: "POST" },
      ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: saasKeys.all });
      toast.success(`Billing cycle ran — ${r.invoiced_count} invoice(s)`);
    },
    onError: (err: Error) => toast.error(`Billing run failed: ${err.message}`),
  });

  const runRecognition = useMutation({
    mutationFn: async () =>
      api<{ posted_count: number; total_recognized: string }>(
        "/api/saas/recognition/run",
        { method: "POST" },
      ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: saasKeys.all });
      toast.success(
        `Posted ${r.posted_count} recognition entries (${r.total_recognized})`,
      );
    },
    onError: (err: Error) => toast.error(`Recognition failed: ${err.message}`),
  });

  const runDunning = useMutation({
    mutationFn: async () =>
      api<{ retried: string[]; abandoned: string[] }>("/api/saas/dunning/run", {
        method: "POST",
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: saasKeys.all });
      const msg = `Dunning cycle: ${r.retried.length} retried, ${r.abandoned.length} abandoned`;
      r.abandoned.length > 0 ? toast.error(msg) : toast.success(msg);
    },
    onError: (err: Error) => toast.error(`Dunning cycle failed: ${err.message}`),
  });

  const snapshotColumns = useMemo<GridColDef<MetricsSnapshot>[]>(() => [
    { field: "snapshot_date", headerName: "Date", width: 120 },
    {
      field: "mrr", headerName: "MRR", width: 130,
      renderCell: (p) => (
        <span className="font-mono">
          {Number(p.row.mrr).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} {p.row.base_currency_code}
        </span>
      ),
    },
    {
      field: "arr", headerName: "ARR", width: 140,
      renderCell: (p) => (
        <span className="font-mono">
          {Number(p.row.arr).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} {p.row.base_currency_code}
        </span>
      ),
    },
    { field: "active_customers", headerName: "Active cust.", width: 110 },
    { field: "new_customers", headerName: "New", width: 70 },
    { field: "churned_customers", headerName: "Churned", width: 90 },
    { field: "active_subscriptions", headerName: "Active subs", width: 110 },
    { field: "trialing_subscriptions", headerName: "Trialing", width: 90 },
    {
      field: "customer_churn_rate", headerName: "Churn rate", width: 110,
      renderCell: (p) =>
        p.row.customer_churn_rate
          ? `${(Number(p.row.customer_churn_rate) * 100).toFixed(2)}%`
          : "—",
    },
  ], []);

  return (
    // Cards + action buttons stay fixed at top; snapshot grid below takes
    // remaining height with internal scroll. The shrink-0 wrapper prevents
    // flex from squeezing the cards when the grid grows.
    <div className="flex flex-col h-full px-6 py-4 gap-4">
      {/* Live MRR / ARR cards */}
      <div className="grid grid-cols-2 gap-4 shrink-0">
        <div className="bg-white border border-slate-200 rounded-lg p-4 dark:bg-gray-900 dark:border-gray-800">
          <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Live MRR
          </div>
          <div className="text-3xl font-semibold text-slate-900 mt-1 font-mono dark:text-slate-100">
            {mrrQuery.data
              ? Number(mrrQuery.data.mrr).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "—"}
          </div>
          <div className="text-xs text-slate-500 mt-1 dark:text-slate-400">
            Computed from active subscriptions, normalized to monthly.
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4 dark:bg-gray-900 dark:border-gray-800">
          <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Live ARR
          </div>
          <div className="text-3xl font-semibold text-slate-900 mt-1 font-mono dark:text-slate-100">
            {arrQuery.data
              ? Number(arrQuery.data.arr).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "—"}
          </div>
          <div className="text-xs text-slate-500 mt-1 dark:text-slate-400">
            MRR × 12. Doesn't account for one-time charges.
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 flex-wrap shrink-0">
        <button
          onClick={() => takeSnapshot.mutate()}
          disabled={takeSnapshot.isPending}
          className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium disabled:opacity-50"
          title="Compute today's MRR/ARR + customer counts and persist as a snapshot"
        >
          {takeSnapshot.isPending ? "Snapshotting…" : "📸 Take snapshot"}
        </button>
        <button
          onClick={() => runBilling.mutate()}
          disabled={runBilling.isPending}
          className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-3 py-1.5 font-medium disabled:opacity-50"
          title="Issue AR invoices for every subscription whose next_invoice_date is today or earlier"
        >
          {runBilling.isPending ? "Running…" : "💸 Run billing cycle"}
        </button>
        <button
          onClick={() => runRecognition.mutate()}
          disabled={runRecognition.isPending}
          className="text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 rounded-md px-3 py-1.5 font-medium disabled:opacity-50 dark:bg-gray-800 dark:border-gray-700 dark:text-slate-300"
          title="Post any due revenue-recognition journal entries"
        >
          {runRecognition.isPending ? "Posting…" : "📅 Run recognition"}
        </button>
        <button
          onClick={() => runDunning.mutate()}
          disabled={runDunning.isPending}
          className="text-sm bg-red-50 hover:bg-red-100 text-red-700 border border-red-300 rounded-md px-3 py-1.5 font-medium disabled:opacity-50 dark:bg-red-950/30 dark:border-red-800 dark:text-red-400"
          title="Process all due dunning retries — schedule next attempts or cancel abandoned subscriptions"
        >
          {runDunning.isPending ? "Running…" : "⚠ Run dunning cycle"}
        </button>
      </div>

      {/* Snapshot history — flex-1 + min-h-0 lets it consume the rest of
          the metrics-tab vertical space. Header stays pinned within the
          grid as the user scrolls older snapshots. */}
      <div className="flex flex-col flex-1 min-h-0">
        <h2 className="text-sm font-semibold text-slate-700 mb-2 shrink-0 dark:text-slate-300">
          Snapshot history
        </h2>
        <div className="flex-1 min-h-0">
          <FlexGrid>
            <DataGridPro
              {...gridDefaults}
              rows={snapshotsQuery.data ?? []}
              columns={snapshotColumns}
              getRowId={(r) => r.id}
              loading={snapshotsQuery.isLoading}
              density="compact"
              disableRowSelectionOnClick
            />
          </FlexGrid>
        </div>
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Revenue Recognition tab
// ──────────────────────────────────────────────────────────────
import { saasRecognitionSchedulesQueryOptions } from "@/lib/queries";
import type { RecognitionSchedule } from "@/lib/types";

function RevRecTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery(saasRecognitionSchedulesQueryOptions());
  const schedules = q.data ?? [];

  const runRecognition = useMutation({
    mutationFn: async () =>
      api<{ posted_count: number; total_recognized: string }>(
        "/api/saas/recognition/run", { method: "POST", body: {} },
      ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: saasKeys.all });
      toast.success(
        `Posted ${r.posted_count} recognition entry(ies), total $${r.total_recognized}`,
      );
    },
    onError: (err: Error) => toast.error(`Recognition failed: ${err.message}`),
  });

  // Aggregate across all schedules
  const totals = useMemo(() => {
    let recognizedToDate = 0;
    let deferredRemaining = 0;
    let scheduledCount = 0;
    let postedCount = 0;
    const byMonth: Record<string, number> = {};

    for (const s of schedules) {
      const total = Number(s.total_amount);
      for (const e of s.entries) {
        const amt = Number(e.amount);
        if (e.status === "posted") {
          recognizedToDate += amt;
          postedCount++;
          const month = e.recognize_on.slice(0, 7); // YYYY-MM
          byMonth[month] = (byMonth[month] ?? 0) + amt;
        } else if (e.status === "scheduled") {
          deferredRemaining += amt;
          scheduledCount++;
        }
      }
      void total;
    }
    // Sort months asc for the chart
    const monthly = Object.entries(byMonth)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, amount]) => ({ month, amount }));

    return { recognizedToDate, deferredRemaining, scheduledCount, postedCount, monthly };
  }, [schedules]);

  const fmt = (n: number): string =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="h-full overflow-auto px-6 py-4">
      {/* KPI strip */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Kpi label="Recognized to date"
             value={`$${fmt(totals.recognizedToDate)}`}
             color="text-emerald-700" />
        <Kpi label="Deferred revenue (on BS)"
             value={`$${fmt(totals.deferredRemaining)}`}
             color="text-amber-700" />
        <Kpi label="Schedules"
             value={`${schedules.length}`}
             color="text-slate-700" />
        <Kpi label="Entries scheduled / posted"
             value={`${totals.scheduledCount} / ${totals.postedCount}`}
             color="text-slate-700" />
      </div>

      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            ASC 606 / subscription revenue recognition
          </h2>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Each subscription invoice creates a schedule with N monthly entries.
            When a period closes, the entry posts: Dr Deferred Revenue / Cr Revenue.
          </div>
        </div>
        <button
          onClick={() => runRecognition.mutate()}
          disabled={runRecognition.isPending}
          className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium disabled:opacity-50"
          title="Post all entries whose recognize_on date is on or before today"
        >
          {runRecognition.isPending ? "Running…" : "Run recognition"}
        </button>
      </div>

      {/* Monthly recognized */}
      {totals.monthly.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md mb-6 overflow-hidden dark:bg-gray-900 dark:border-gray-800">
          <div className="bg-slate-50 px-4 py-2 text-xs uppercase tracking-wide text-slate-600 font-semibold border-b border-slate-200 dark:bg-gray-950 dark:border-gray-800 dark:text-slate-300">
            Recognized by month
          </div>
          <div className="p-4">
            {totals.monthly.slice(-12).map((m) => {
              const maxAmount = Math.max(...totals.monthly.map((x) => x.amount));
              const pct = maxAmount > 0 ? (m.amount / maxAmount) * 100 : 0;
              return (
                <div key={m.month} className="flex items-center gap-3 mb-1 text-xs">
                  <span className="w-20 text-slate-600 font-mono dark:text-slate-300">{m.month}</span>
                  <div className="flex-1 bg-slate-100 rounded h-5 relative dark:bg-gray-800">
                    <div
                      className="bg-emerald-500 h-full rounded"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="w-28 text-right font-mono text-slate-800 dark:text-slate-200">
                    ${fmt(m.amount)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Schedule detail */}
      <div className="bg-white border border-slate-200 rounded-md dark:bg-gray-900 dark:border-gray-800">
        <div className="bg-slate-50 px-4 py-2 text-xs uppercase tracking-wide text-slate-600 font-semibold border-b border-slate-200 dark:bg-gray-950 dark:border-gray-800 dark:text-slate-300">
          Active schedules
        </div>
        {schedules.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm dark:text-slate-400">
            No recognition schedules yet.
            <br />
            <span className="text-xs text-slate-400 dark:text-slate-500">
              Schedules are created automatically when a subscription invoice posts.
            </span>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white text-xs uppercase tracking-wide text-slate-600 border-b border-slate-200 dark:bg-gray-900 dark:border-gray-800 dark:text-slate-300">
              <tr>
                <th className="text-left px-4 py-2">Service window</th>
                <th className="text-right px-4 py-2">Total</th>
                <th className="text-right px-4 py-2">Recognized</th>
                <th className="text-right px-4 py-2">Remaining</th>
                <th className="text-left px-4 py-2">Progress</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map((s: RecognitionSchedule) => {
                const total = Number(s.total_amount);
                const posted = s.entries
                  .filter((e) => e.status === "posted")
                  .reduce((sum, e) => sum + Number(e.amount), 0);
                const remaining = total - posted;
                const pct = total > 0 ? (posted / total) * 100 : 0;
                const postedCount = s.entries.filter(
                  (e) => e.status === "posted",
                ).length;
                return (
                  <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50 dark:border-gray-800 dark:hover:bg-gray-800">
                    <td className="px-4 py-2 text-xs">
                      <div className="text-slate-700 dark:text-slate-300">
                        {s.service_start} → {s.service_end}
                      </div>
                      <div className="text-[10px] text-slate-500 dark:text-slate-400">
                        {s.period_count} periods · {s.currency_code}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right font-mono">
                      ${fmt(total)}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-emerald-700">
                      ${fmt(posted)}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-amber-700">
                      ${fmt(remaining)}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-slate-200 h-2 rounded overflow-hidden min-w-[100px] dark:bg-gray-700">
                          <div
                            className="bg-emerald-500 h-full"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-[10px] text-slate-600 font-mono w-14 text-right dark:text-slate-300">
                          {postedCount}/{s.period_count}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


function Kpi({
  label, value, color,
}: {
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 dark:bg-gray-900 dark:border-gray-800">
      <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className={`text-xl font-semibold font-mono mt-1 ${color}`}>{value}</div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Tax Rules tab
// ──────────────────────────────────────────────────────────────
const EMPTY_RULE: TaxRuleCreate = {
  jurisdiction_code: "",
  product_code: null,
  tax_rate: "",
  effective_from: new Date().toISOString().slice(0, 10),
  effective_to: null,
  description: null,
};

function TaxRulesTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirmAction();
  const [form, setForm] = useState<TaxRuleCreate>(EMPTY_RULE);
  const [showForm, setShowForm] = useState(false);

  const rulesQuery = useQuery(saasTaxRulesQueryOptions());

  const createRule = useMutation({
    mutationFn: async (payload: TaxRuleCreate) =>
      api<TaxRule>("/api/saas/tax-rules", { method: "POST", body: payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...["saas"], "tax-rules"] });
      toast.success("Tax rule added");
      setForm(EMPTY_RULE);
      setShowForm(false);
    },
    onError: (err: Error) => toast.error(`Failed: ${err.message}`),
  });

  const deleteRule = useMutation({
    mutationFn: async (id: string) =>
      api(`/api/saas/tax-rules/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...["saas"], "tax-rules"] });
      toast.success("Tax rule deleted");
    },
    onError: (err: Error) => toast.error(`Failed: ${err.message}`),
  });

  const requestDelete = (rule: TaxRule) => {
    confirmAction({
      title: "Delete tax rule?",
      message: `${rule.jurisdiction_code}${rule.product_code ? " / " + rule.product_code : ""} — ${(Number(rule.tax_rate) * 100).toFixed(4)}%`,
      confirmLabel: "Delete",
      kind: "danger",
      onConfirm: async () => { await deleteRule.mutateAsync(rule.id); },
    });
  };

  const fmtRate = (r: string) => `${(Number(r) * 100).toFixed(4)}%`;

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-3xl mx-auto flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">
              Sales-tax rules
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Entity-scoped rules by jurisdiction + product code. Most-specific match wins.
              Rate is fractional (0.0875 = 8.75%).
            </p>
          </div>
          <button
            onClick={() => setShowForm((v) => !v)}
            className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium"
          >
            {showForm ? "Cancel" : "+ Add rule"}
          </button>
        </div>

        {/* Add rule form */}
        {showForm && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 dark:bg-blue-950/30 dark:border-blue-900">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Jurisdiction code *
                </label>
                <input
                  type="text"
                  placeholder="US-CA, EU-VAT, AU-GST…"
                  value={form.jurisdiction_code}
                  onChange={(e) => setForm({ ...form, jurisdiction_code: e.target.value })}
                  className="text-sm border border-slate-200 rounded px-2 py-1.5 dark:bg-gray-900 dark:border-gray-700"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Product code (blank = all)
                </label>
                <input
                  type="text"
                  placeholder="plan code or leave blank"
                  value={form.product_code ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, product_code: e.target.value || null })
                  }
                  className="text-sm border border-slate-200 rounded px-2 py-1.5 dark:bg-gray-900 dark:border-gray-700"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Tax rate * (fractional, e.g. 0.0875)
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  max="1"
                  placeholder="0.0875"
                  value={form.tax_rate}
                  onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                  className="text-sm border border-slate-200 rounded px-2 py-1.5 dark:bg-gray-900 dark:border-gray-700"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Effective from *
                </label>
                <input
                  type="date"
                  value={form.effective_from}
                  onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                  className="text-sm border border-slate-200 rounded px-2 py-1.5 dark:bg-gray-900 dark:border-gray-700"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Effective to (blank = open-ended)
                </label>
                <input
                  type="date"
                  value={form.effective_to ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, effective_to: e.target.value || null })
                  }
                  className="text-sm border border-slate-200 rounded px-2 py-1.5 dark:bg-gray-900 dark:border-gray-700"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Description
                </label>
                <input
                  type="text"
                  placeholder="e.g. California digital services tax"
                  value={form.description ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value || null })
                  }
                  className="text-sm border border-slate-200 rounded px-2 py-1.5 dark:bg-gray-900 dark:border-gray-700"
                />
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <button
                onClick={() => createRule.mutate(form)}
                disabled={
                  createRule.isPending ||
                  !form.jurisdiction_code ||
                  !form.tax_rate ||
                  !form.effective_from
                }
                className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-1.5 font-medium disabled:opacity-50"
              >
                {createRule.isPending ? "Saving…" : "Save rule"}
              </button>
            </div>
          </div>
        )}

        {/* Rules table */}
        {rulesQuery.isLoading ? (
          <div className="text-sm text-slate-500 dark:text-slate-400 py-4">Loading…</div>
        ) : (rulesQuery.data ?? []).length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg px-4 py-8 text-center dark:bg-gray-900 dark:border-gray-800">
            <div className="text-sm text-slate-500 dark:text-slate-400">
              No tax rules configured.
            </div>
            <div className="text-xs text-slate-400 dark:text-slate-500 mt-1">
              Add rules for jurisdictions where you collect sales tax.
              Zero rules = 0% tax applied everywhere.
            </div>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden dark:bg-gray-900 dark:border-gray-800">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-600 border-b border-slate-100 bg-slate-50 dark:bg-gray-950 dark:border-gray-800 dark:text-slate-300">
                <tr>
                  <th className="text-left px-4 py-2">Jurisdiction</th>
                  <th className="text-left px-4 py-2">Product</th>
                  <th className="text-right px-4 py-2">Rate</th>
                  <th className="text-left px-4 py-2">Effective from</th>
                  <th className="text-left px-4 py-2">Effective to</th>
                  <th className="text-left px-4 py-2">Description</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {(rulesQuery.data ?? []).map((rule) => (
                  <tr key={rule.id} className="border-t border-slate-100 dark:border-gray-800 hover:bg-slate-50 dark:hover:bg-gray-800">
                    <td className="px-4 py-2 font-mono text-xs font-semibold text-slate-700 dark:text-slate-300">
                      {rule.jurisdiction_code}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600 dark:text-slate-400">
                      {rule.product_code ?? (
                        <span className="text-slate-400 dark:text-slate-500 italic">all</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-sm text-slate-800 dark:text-slate-200">
                      {fmtRate(rule.tax_rate)}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600 dark:text-slate-400">
                      {rule.effective_from}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
                      {rule.effective_to ?? (
                        <span className="text-emerald-600 dark:text-emerald-400">current</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
                      {rule.description ?? "—"}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => requestDelete(rule)}
                        className="text-xs text-red-500 hover:text-red-700 dark:text-red-400"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
